import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Download, Loader2, Filter, ChevronLeft, ChevronRight, Play, Pause,
  ChevronDown, ChevronUp, Columns, Info, ArrowUpDown
} from 'lucide-react';
import apiClient from '../../services/apiClient';

// Audio Player Component
const InlineAudioPlayer = ({ url }) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleEnded = () => {
    setIsPlaying(false);
  };

  if (!url) return <span className="text-text-muted">-</span>;

  return (
    <div className="flex items-center gap-2">
      <button
        onClick={togglePlay}
        className="text-primary hover:bg-primary/10 p-1 rounded transition-colors"
        title={isPlaying ? 'Pause' : 'Play'}
      >
        {isPlaying ? <Pause size={16} /> : <Play size={16} />}
      </button>
      <audio ref={audioRef} src={url} onEnded={handleEnded} preload="none" />
    </div>
  );
};

// Tooltip component
const Tooltip = ({ children, content }) => {
  const [show, setShow] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);

  const handleMouseEnter = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + 8,
        left: rect.left + rect.width / 2
      });
    }
    setShow(true);
  };

  return (
    <>
      <span
        ref={triggerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={() => setShow(false)}
        className="cursor-help inline-flex items-center"
      >
        {children}
      </span>
      {show && (
        <div
          className="fixed px-3 py-2 bg-secondary text-white text-xs rounded shadow-lg z-[9999] max-w-xs"
          style={{
            top: position.top,
            left: position.left,
            transform: 'translateX(-50%)'
          }}
        >
          {content}
        </div>
      )}
    </>
  );
};

// Column definitions
const ALL_COLUMNS = [
  { id: 'inspectionId', labelKey: 'admin.inspection_id', sortable: false, defaultVisible: true },
  { id: 'createdAt', labelKey: 'admin.date', sortable: true, defaultVisible: true },
  { id: 'userEmail', labelKey: 'admin.user_email', sortable: true, defaultVisible: true },
  { id: 'vehicleId', labelKey: 'admin.vehicle', sortable: true, defaultVisible: true },
  { id: 'status', labelKey: 'admin.status', sortable: true, defaultVisible: true },
  { id: 'inputDevice', labelKey: 'admin.input', sortable: false, defaultVisible: true },
  { id: 'os', labelKey: 'admin.os', sortable: false, defaultVisible: false },
  { id: 'browser', labelKey: 'admin.browser', sortable: false, defaultVisible: false },
  { id: 'isMobile', labelKey: 'admin.device_type', sortable: false, defaultVisible: false },
  { id: 'audio', labelKey: 'admin.audio', sortable: false, defaultVisible: true },
  { id: 'duration', labelKey: 'admin.duration', sortable: true, defaultVisible: true },
  { id: 'rawTranscript', labelKey: 'admin.raw_transcript', sortable: false, defaultVisible: true },
  { id: 'processedTranscript', labelKey: 'admin.enhanced_text', sortable: false, defaultVisible: true },
  { id: 'accuracy', labelKey: 'admin.accuracy', sortable: true, defaultVisible: true },
  { id: 'fidelity', labelKey: 'admin.fidelity', sortable: true, defaultVisible: true },
];

const InspectionsTab = () => {
  const { t } = useTranslation();

  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(20);

  // Sorting
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');

  // Filters
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    vehicleId: '',
    minAccuracyScore: '',
    minFidelityScore: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  // Column visibility
  const [visibleColumns, setVisibleColumns] = useState(
    ALL_COLUMNS.filter(c => c.defaultVisible).map(c => c.id)
  );
  const [showColumnSelector, setShowColumnSelector] = useState(false);

  // Expanded rows for showing quality reasoning
  const [expandedRows, setExpandedRows] = useState(new Set());

  useEffect(() => {
    fetchInspections();
  }, [page, pageSize, sortBy, sortOrder]);

  const fetchInspections = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('pageSize', pageSize);
      params.append('sortBy', sortBy);
      params.append('sortOrder', sortOrder);

      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      if (filters.vehicleId) params.append('vehicleId', filters.vehicleId);
      if (filters.minAccuracyScore) params.append('minAccuracyScore', filters.minAccuracyScore);
      if (filters.minFidelityScore) params.append('minFidelityScore', filters.minFidelityScore);

      const response = await apiClient.get(`/admin/inspections?${params.toString()}`);
      setInspections(response.data.items || []);
      setTotalPages(response.data.totalPages || 1);
      setTotal(response.data.total || 0);
    } catch (err) {
      console.error('Error fetching admin inspections:', err);
      if (err.response?.status === 403) {
        setError(t('admin.access_denied'));
      } else {
        setError(err.message || t('common.error'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      if (filters.vehicleId) params.append('vehicleId', filters.vehicleId);

      const response = await apiClient.get(`/admin/inspections/export?${params.toString()}`, {
        responseType: 'text'
      });

      const binaryString = atob(response.data);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      const blob = new Blob([bytes], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `quality-review-${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting:', err);
      setError(t('admin.export_error'));
    } finally {
      setExporting(false);
    }
  };

  const applyFilters = () => {
    setPage(1);
    fetchInspections();
    setShowFilters(false);
  };

  const clearFilters = () => {
    setFilters({
      startDate: '',
      endDate: '',
      vehicleId: '',
      minAccuracyScore: '',
      minFidelityScore: ''
    });
    setPage(1);
    fetchInspections();
  };

  const handleSort = (columnId) => {
    if (sortBy === columnId) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(columnId);
      setSortOrder('desc');
    }
  };

  const toggleColumn = (columnId) => {
    setVisibleColumns(prev =>
      prev.includes(columnId)
        ? prev.filter(id => id !== columnId)
        : [...prev, columnId]
    );
  };

  const toggleRowExpanded = (inspectionId) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(inspectionId)) {
        newSet.delete(inspectionId);
      } else {
        newSet.add(inspectionId);
      }
      return newSet;
    });
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-success';
    if (score >= 60) return 'text-warning';
    return 'text-error';
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDuration = (seconds) => {
    if (!seconds && seconds !== 0) return '-';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getStatusBadge = (status) => {
    const statusStyles = {
      'COMPLETED': 'badge-success',
      'TRANSCRIBING': 'badge-warning',
      'ENHANCING': 'badge-warning',
      'FAILED': 'badge-error',
      'UPLOADED': 'badge-info',
      'PENDING': 'badge-warning',
    };
    return statusStyles[status] || 'badge-info';
  };

  const renderCell = (insp, columnId) => {
    switch (columnId) {
      case 'inspectionId':
        // Show first 7 chars of inspection ID as a hash
        const shortId = insp.inspectionId?.substring(0, 7) || '-';
        return (
          <span className="font-mono text-xs" title={insp.inspectionId}>
            {shortId}
          </span>
        );

      case 'createdAt':
        return <span className="whitespace-nowrap">{formatDateTime(insp.createdAt)}</span>;

      case 'userEmail':
        return <span className="text-sm">{insp.userEmail || '-'}</span>;

      case 'vehicleId':
        return <span className="font-mono">{insp.vehicleId || '-'}</span>;

      case 'status':
        return (
          <span className={`badge ${getStatusBadge(insp.status)}`}>
            {t(`status.${insp.status?.toLowerCase()}`, insp.status)}
          </span>
        );

      case 'duration':
        return <span className="font-mono text-sm">{formatDuration(insp.duration)}</span>;

      case 'inputDevice':
        const inputDevice = insp.deviceInfo?.inputDevice;
        // Translate "File Upload" value, display other input devices as-is
        if (inputDevice === 'File Upload' || !inputDevice) {
          return <span className="text-sm">{t('admin.file_upload')}</span>;
        }
        return <span className="text-sm">{inputDevice}</span>;

      case 'os':
        return <span className="text-sm">{insp.deviceInfo?.os || '-'}</span>;

      case 'browser':
        return <span className="text-sm">{insp.deviceInfo?.browser || '-'}</span>;

      case 'isMobile':
        if (insp.deviceInfo?.isMobile === undefined) return '-';
        return (
          <span className={`badge ${insp.deviceInfo.isMobile ? 'badge-info' : 'badge-success'}`}>
            {insp.deviceInfo.isMobile ? t('admin.mobile') : t('admin.desktop')}
          </span>
        );

      case 'audio':
        return <InlineAudioPlayer url={insp.audioUrl} />;

      case 'rawTranscript':
        return (
          <div className="max-w-xs">
            <p className="truncate text-sm" title={insp.rawTranscript}>
              {insp.rawTranscript?.substring(0, 60) || '-'}
              {insp.rawTranscript?.length > 60 && '...'}
            </p>
          </div>
        );

      case 'processedTranscript':
        return (
          <div className="max-w-xs">
            <p className="truncate text-sm" title={insp.processedTranscript}>
              {insp.processedTranscript?.substring(0, 60) || '-'}
              {insp.processedTranscript?.length > 60 && '...'}
            </p>
          </div>
        );

      case 'accuracy':
        if (!insp.qualityScores) return '-';
        return (
          <div className="flex items-center gap-1">
            <span className={getScoreColor(insp.qualityScores.transcriptAccuracy)}>
              {insp.qualityScores.transcriptAccuracy}%
            </span>
            {insp.qualityScores.transcriptFeedback && (
              <button
                onClick={() => toggleRowExpanded(insp.inspectionId)}
                className="text-text-muted hover:text-text p-0.5"
              >
                <Info size={14} />
              </button>
            )}
          </div>
        );

      case 'fidelity':
        if (!insp.qualityScores) return '-';
        return (
          <div className="flex items-center gap-1">
            <span className={getScoreColor(insp.qualityScores.enrichmentFidelity)}>
              {insp.qualityScores.enrichmentFidelity}%
            </span>
            {insp.qualityScores.enrichmentFeedback && (
              <button
                onClick={() => toggleRowExpanded(insp.inspectionId)}
                className="text-text-muted hover:text-text p-0.5"
              >
                <Info size={14} />
              </button>
            )}
          </div>
        );

      default:
        return '-';
    }
  };

  const sortedVisibleColumns = useMemo(() => {
    return ALL_COLUMNS.filter(col => visibleColumns.includes(col.id));
  }, [visibleColumns]);

  const pageSizeOptions = [10, 20, 50, 100];

  return (
    <>
      {/* Actions Bar */}
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="btn-secondary flex items-center gap-2"
          >
            <Filter size={18} />
            {t('admin.filters')}
          </button>

          {/* Column Selector */}
          <div className="relative">
            <button
              onClick={() => setShowColumnSelector(!showColumnSelector)}
              className="btn-secondary flex items-center gap-2"
            >
              <Columns size={18} />
              {t('admin.columns')}
            </button>
            {showColumnSelector && (
              <div className="absolute top-full left-0 mt-1 bg-surface border border-border rounded-lg shadow-lg p-3 z-50 min-w-[200px]">
                {ALL_COLUMNS.map(col => (
                  <label key={col.id} className="flex items-center gap-2 py-1 cursor-pointer hover:bg-background px-2 rounded">
                    <input
                      type="checkbox"
                      checked={visibleColumns.includes(col.id)}
                      onChange={() => toggleColumn(col.id)}
                      className="rounded border-border"
                    />
                    <span className="text-sm">{t(col.labelKey)}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {(filters.startDate || filters.endDate || filters.vehicleId || filters.minAccuracyScore || filters.minFidelityScore) && (
            <button
              onClick={clearFilters}
              className="text-sm text-primary hover:underline"
            >
              {t('admin.clear_filters')}
            </button>
          )}
        </div>
        <button
          onClick={handleExport}
          disabled={exporting || loading}
          className="btn-primary flex items-center gap-2"
        >
          {exporting ? (
            <Loader2 size={18} className="animate-spin" />
          ) : (
            <Download size={18} />
          )}
          {t('admin.export')}
        </button>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="card-elevated p-4 mb-4">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div>
              <label className="block text-xs text-text-muted mb-1">{t('admin.start_date')}</label>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                className="input text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-text-muted mb-1">{t('admin.end_date')}</label>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                className="input text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-text-muted mb-1">{t('admin.vehicle_id')}</label>
              <input
                type="text"
                value={filters.vehicleId}
                onChange={(e) => setFilters({ ...filters, vehicleId: e.target.value })}
                placeholder="B-AB 1234"
                className="input text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-text-muted mb-1">{t('admin.min_accuracy')}</label>
              <input
                type="number"
                min="0"
                max="100"
                value={filters.minAccuracyScore}
                onChange={(e) => setFilters({ ...filters, minAccuracyScore: e.target.value })}
                placeholder="0-100"
                className="input text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-text-muted mb-1">{t('admin.min_fidelity')}</label>
              <input
                type="number"
                min="0"
                max="100"
                value={filters.minFidelityScore}
                onChange={(e) => setFilters({ ...filters, minFidelityScore: e.target.value })}
                placeholder="0-100"
                className="input text-sm"
              />
            </div>
          </div>
          <div className="flex justify-end mt-4">
            <button onClick={applyFilters} className="btn-primary">
              {t('admin.apply_filters')}
            </button>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg mb-4">
          {error}
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 text-primary animate-spin" />
        </div>
      ) : inspections.length === 0 ? (
        <div className="text-center py-12 text-text-muted">
          {t('admin.no_inspections')}
        </div>
      ) : (
        <>
          {/* Results Count & Page Size */}
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-text-muted">
              {t('admin.showing_results', { count: inspections.length, total })}
            </p>
            <div className="flex items-center gap-2">
              <label className="text-sm text-text-muted">{t('admin.items_per_page')}</label>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
                className="input text-sm py-1 px-2 w-20"
              >
                {pageSizeOptions.map(size => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Inspections Table */}
          <div className="card-elevated overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-surface-alt">
                  <tr>
                    {sortedVisibleColumns.map(col => (
                      <th
                        key={col.id}
                        className="px-4 py-3 text-left font-medium text-text-muted"
                      >
                        {col.sortable ? (
                          <button
                            onClick={() => handleSort(col.id)}
                            className="flex items-center gap-1 hover:text-text transition-colors"
                          >
                            {col.id === 'accuracy' ? (
                              <Tooltip content={t('admin.accuracy_tooltip')}>
                                <span className="flex items-center gap-1">
                                  {t(col.labelKey)}
                                  <Info size={12} className="text-text-light" />
                                </span>
                              </Tooltip>
                            ) : col.id === 'fidelity' ? (
                              <Tooltip content={t('admin.fidelity_tooltip')}>
                                <span className="flex items-center gap-1">
                                  {t(col.labelKey)}
                                  <Info size={12} className="text-text-light" />
                                </span>
                              </Tooltip>
                            ) : (
                              t(col.labelKey)
                            )}
                            {sortBy === col.id ? (
                              sortOrder === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                            ) : (
                              <ArrowUpDown size={14} className="opacity-30" />
                            )}
                          </button>
                        ) : (
                          t(col.labelKey)
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {inspections.map((insp) => (
                    <React.Fragment key={insp.inspectionId}>
                      <tr className="hover:bg-surface-alt">
                        {sortedVisibleColumns.map(col => (
                          <td key={col.id} className="px-4 py-3">
                            {renderCell(insp, col.id)}
                          </td>
                        ))}
                      </tr>
                      {/* Expanded row for quality reasoning */}
                      {expandedRows.has(insp.inspectionId) && insp.qualityScores && (
                        <tr className="bg-background">
                          <td colSpan={sortedVisibleColumns.length} className="px-4 py-3">
                            <div className="grid grid-cols-2 gap-4 text-sm">
                              {insp.qualityScores.transcriptFeedback && (
                                <div>
                                  <h4 className="font-medium text-text mb-1">{t('admin.accuracy_reasoning')}</h4>
                                  <p className="text-text-muted">{insp.qualityScores.transcriptFeedback}</p>
                                </div>
                              )}
                              {insp.qualityScores.enrichmentFeedback && (
                                <div>
                                  <h4 className="font-medium text-text mb-1">{t('admin.fidelity_reasoning')}</h4>
                                  <p className="text-text-muted">{insp.qualityScores.enrichmentFeedback}</p>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-center gap-4 mt-6">
            <button
              onClick={() => setPage(1)}
              disabled={page === 1}
              className="btn-secondary px-3 py-2 disabled:opacity-50 text-sm"
            >
              {t('admin.first')}
            </button>
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="btn-secondary p-2 disabled:opacity-50"
            >
              <ChevronLeft size={20} />
            </button>
            <span className="text-sm text-text">
              {t('admin.page_of', { page, total: totalPages })}
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="btn-secondary p-2 disabled:opacity-50"
            >
              <ChevronRight size={20} />
            </button>
            <button
              onClick={() => setPage(totalPages)}
              disabled={page === totalPages}
              className="btn-secondary px-3 py-2 disabled:opacity-50 text-sm"
            >
              {t('admin.last')}
            </button>
          </div>
        </>
      )}
    </>
  );
};

export default InspectionsTab;
